import { FunctionComponent, useEffect, useState } from 'react'
import { Location, Navigate, useLocation } from 'react-router-dom'
import styles from './auth.module.scss'
import LoggedOutNav from '@/components/Nav/LoggedOutNav/LoggedOutNav'
import Welcome from './welcome'
import Login from './login'
import { useLazyQuery } from '@apollo/client'
import Loading from '@/components/Loading/Loading'
import { GET_VERIFY_AUTO_LINK, GET_VERIFY_EMAIL } from '@/modules/API/account/accountHandler'
import Signup from './signup'
import Footer from './footer'
import { useAuth } from '@/modules/context/AuthContext'
import SignupMobile from './signupMobile'
import MainLoading from '../other/mainLoading'
import UseAutoLinkLogged from './useAutoLinkLogged'
import { setUserInterfaceDaycareColors } from '@/modules/helper/customization.ts'

const Auth: FunctionComponent = () => {
    const location: Location = useLocation()

    const params: URLSearchParams = new URLSearchParams(location.search)

    const [welcome, setWelcome] = useState<boolean>(true)
    const [isKnown, setIsKnown] = useState<boolean>(false)

    const [autoLink, setAutoLink] = useState<string | undefined>(undefined)

    const [email, setEmail] = useState<string>('')

    const [role, setRole] = useState<string>('')

    const [justSignedUp, setJustSignedUp] = useState<boolean>(false)

    useEffect((): void => {
        const paramsAutolink: string | null = params.get('autoLink')
        if (paramsAutolink) {
            setAutoLink(paramsAutolink)
            getVerifyAutolink({
                variables: { autoLink: paramsAutolink }
            })
        }

        if (params.get('signup')) {
            setEmail(params.get('signup') || '')
            searchMail()
        }

        if (params.get('role')) {
            const roleRequested: string = params.get('role') || ''
            if (roleRequested === 'parent' || roleRequested === 'staff' || roleRequested === 'assmat') {
                setRole(roleRequested)
            }
        }
    }, [])

    function returnToLogin(): void {
        setIsKnown(true)
        setWelcome(true)
    }

    const [getVerifyEmail, { loading: loadingVerifyMail, data: dataVerifyEmail, error: errorVerifyEmail }] =
        useLazyQuery(GET_VERIFY_EMAIL, {
            variables: {
                email: email
            }
        })

    useEffect(() => {
        if (errorVerifyEmail) {
            setIsKnown(false)
        }
    }, [errorVerifyEmail])

    useEffect(() => {
        if (dataVerifyEmail) {
            setIsKnown(true)
        }
    }, [dataVerifyEmail])

    const [
        getVerifyAutolink,
        { loading: loadingVerifyAutolink, data: dataVerifyAutolink, error: errorVerifyAutolink }
    ] = useLazyQuery(GET_VERIFY_AUTO_LINK, {
        variables: {
            autoLink
        }
    })

    const [logo, setLogo] = useState<string | undefined>(undefined)

    useEffect(() => {
        if (dataVerifyAutolink) {
            if (dataVerifyAutolink.getVerifyAutoLink?.colors) {
                setUserInterfaceDaycareColors(dataVerifyAutolink.getVerifyAutoLink.colors)
            }
            if (dataVerifyAutolink.getVerifyAutoLink?.logoBase64Data) {
                setLogo(dataVerifyAutolink.getVerifyAutoLink.logoBase64Data)
            }
        }
    }, [dataVerifyAutolink])

    useEffect(() => {
        if (errorVerifyAutolink) setAutoLink(undefined)
    }, [errorVerifyAutolink])

    async function searchMail(): Promise<void> {
        await getVerifyEmail()
        setWelcome(false)
    }

    function resetSelectedRole(): void {
        setRole('')
    }

    const { user, device } = useAuth()

    if (!device) {
        return <MainLoading />
    }

    if (!user) {
        return (
            <div className={styles.PagePosition}>
                <LoggedOutNav />
                <div className={styles.LoginPlacement}>
                    {(loadingVerifyMail || loadingVerifyAutolink) && <Loading overall />}
                    {welcome && (
                        <>
                            <Welcome searchMail={searchMail} email={email} setEmail={setEmail} logo={logo} />
                            <Footer />
                        </>
                    )}
                    {!welcome && isKnown && (
                        <>
                            <Login
                                returnToLogin={returnToLogin}
                                email={email}
                                setEmail={setEmail}
                                deviceType={device?.platform}
                                autoLink={autoLink}
                                logo={logo}
                            />
                            <Footer />
                        </>
                    )}
                    {!welcome &&
                        !isKnown &&
                        (device?.platform === 'web' ? (
                            <Signup
                                returnToLogin={returnToLogin}
                                email={email}
                                setEmail={setEmail}
                                autoLink={autoLink}
                                roleRequested={role}
                                resetSelectedRole={resetSelectedRole}
                                setJustSignedUp={setJustSignedUp}
                                logo={logo}
                            />
                        ) : (
                            <SignupMobile returnToLogin={returnToLogin} email={email} role={role} />
                        ))}
                </div>
            </div>
        )
    }

    if (user && autoLink && user.currentDaycare === null) {
        return <UseAutoLinkLogged autoLink={autoLink} role={user.role} />
    }

    if (justSignedUp) {
        return (
            <Navigate
                to={`/account/profile?account=signup${autoLink !== undefined && autoLink !== '' ? 'Autolink' : ''}`}
            />
        )
    }

    return <Navigate to='/calendar' />
}

export default Auth
